import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { API, errMsg, TOPIC_NAMES } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";

const LETTERS = ["A", "B", "C", "D", "E"];
const QTYPES = ["diagnosis", "investigation", "management", "pharmacology", "mechanism", "interpretation",
  "complication", "next step", "professionalism", "communication", "emergency", "risk", "preventive"];

const EMPTY = {
  topic: "cardiology", subtopic: "", difficulty: "medium", question_type: "diagnosis",
  stem: "", options: { A: "", B: "", C: "", D: "", E: "" }, correct_answer: "A",
  explanation_correct: "", option_explanations: { A: "", B: "", C: "", D: "", E: "" },
  key_learning_point: "", reference: "", curriculum_mapping: "", para_mapping: "",
  tags: "", learning_objective: "", condition: "", is_demo: false,
};

export default function AdminQuestionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("draft");
  const [objectiveCount, setObjectiveCount] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    API.get(`/admin/questions/${id}`).then(({ data }) => {
      setForm({
        topic: data.topic, subtopic: data.subtopic || "", difficulty: data.difficulty,
        question_type: data.question_type || "diagnosis", stem: data.stem,
        options: data.options, correct_answer: data.correct_answer,
        explanation_correct: data.explanation_correct || "",
        option_explanations: { A: "", B: "", C: "", D: "", E: "", ...(data.option_explanations || {}) },
        key_learning_point: data.key_learning_point || "", reference: data.reference || "",
        curriculum_mapping: data.curriculum_mapping || "", para_mapping: data.para_mapping || "",
        tags: (data.tags || []).join(", "), learning_objective: data.learning_objective || "",
        condition: data.condition || "", is_demo: !!data.is_demo,
      });
      setStatus(data.status);
      setObjectiveCount(data.objective_count || 0);
    }).catch((e) => { toast.error(errMsg(e)); navigate("/admin/questions"); });
  }, [id, isNew, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      const { data } = isNew
        ? await API.post("/admin/questions", payload)
        : await API.put(`/admin/questions/${id}`, payload);
      if (data.objective_count > 5) {
        toast.warning(`Note: ${data.objective_count} active questions now test this learning objective`, { duration: 6000 });
      } else {
        toast.success(isNew ? `Created ${data.qid} as draft` : "Question saved");
      }
      navigate("/admin/questions");
    } catch (e) {
      toast.error(errMsg(e));
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-question-editor" className="max-w-4xl">
      <button data-testid="editor-back-btn" onClick={() => navigate("/admin/questions")}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> All questions
      </button>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="font-display text-2xl font-bold" data-testid="editor-title">{isNew ? "New question" : "Edit question"}</h1>
        {!isNew && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-muted capitalize">{status.replace("_", " ")}</span>}
      </div>

      {objectiveCount > 5 && (
        <div className="mb-6 flex items-start gap-3 bg-accent/20 border border-accent rounded-lg p-4" data-testid="objective-warning">
          <AlertTriangle className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm">Warning: there are already <strong>{objectiveCount}</strong> active questions testing this learning objective. Repetition can be educational, but check this adds new value.</p>
        </div>
      )}

      <div className="space-y-6">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Topic</Label>
            <Select value={form.topic} onValueChange={(v) => set("topic", v)}>
              <SelectTrigger data-testid="editor-topic"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TOPIC_NAMES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`editor-topic-${k}`}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Subtopic</Label>
            <Input data-testid="editor-subtopic" value={form.subtopic} onChange={(e) => set("subtopic", e.target.value)} placeholder="e.g. Arrhythmias" />
          </div>
          <div className="space-y-2">
            <Label>Difficulty</Label>
            <Select value={form.difficulty} onValueChange={(v) => set("difficulty", v)}>
              <SelectTrigger data-testid="editor-difficulty"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["easy", "medium", "hard"].map((d) => <SelectItem key={d} value={d} data-testid={`editor-difficulty-${d}`} className="capitalize">{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Question type</Label>
            <Select value={form.question_type} onValueChange={(v) => set("question_type", v)}>
              <SelectTrigger data-testid="editor-qtype"><SelectValue /></SelectTrigger>
              <SelectContent>
                {QTYPES.map((t) => <SelectItem key={t} value={t} data-testid={`editor-qtype-${t.replace(/ /g, "-")}`} className="capitalize">{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Condition</Label>
            <Input data-testid="editor-condition" value={form.condition} onChange={(e) => set("condition", e.target.value)} placeholder="e.g. Atrial fibrillation" />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Question stem</Label>
          <Textarea data-testid="editor-stem" rows={5} value={form.stem} onChange={(e) => set("stem", e.target.value)}
            placeholder="Clinical scenario followed by the question…" />
        </div>

        <div className="space-y-3">
          <Label>Answer options (best of five)</Label>
          {LETTERS.map((l) => (
            <div key={l} className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-sm font-bold shrink-0">{l}</span>
              <Input data-testid={`editor-option-${l}`} value={form.options[l]}
                onChange={(e) => set("options", { ...form.options, [l]: e.target.value })} placeholder={`Option ${l}`} />
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <Label>Correct answer</Label>
          <Select value={form.correct_answer} onValueChange={(v) => set("correct_answer", v)}>
            <SelectTrigger className="w-32" data-testid="editor-correct"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LETTERS.map((l) => <SelectItem key={l} value={l} data-testid={`editor-correct-${l}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Why the correct answer is correct</Label>
          <Textarea data-testid="editor-explanation" rows={3} value={form.explanation_correct}
            onChange={(e) => set("explanation_correct", e.target.value)} />
        </div>

        <div className="space-y-3">
          <Label>Why each option is wrong (leave the correct option blank or use it for the main explanation)</Label>
          {LETTERS.map((l) => (
            <div key={l} className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-full border border-border flex items-center justify-center text-sm font-bold shrink-0 mt-1">{l}</span>
              <Textarea data-testid={`editor-wex-${l}`} rows={2} value={form.option_explanations[l]}
                onChange={(e) => set("option_explanations", { ...form.option_explanations, [l]: e.target.value })}
                placeholder={`Why ${l} is incorrect…`} />
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Key learning point</Label>
            <Textarea data-testid="editor-klp" rows={2} value={form.key_learning_point} onChange={(e) => set("key_learning_point", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Reference (e.g. NICE NG196)</Label>
            <Textarea data-testid="editor-reference" rows={2} value={form.reference} onChange={(e) => set("reference", e.target.value)} />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Learning objective</Label>
            <Input data-testid="editor-objective" value={form.learning_objective} onChange={(e) => set("learning_objective", e.target.value)}
              placeholder="What this question teaches" />
          </div>
          <div className="space-y-2">
            <Label>Tags (comma separated)</Label>
            <Input data-testid="editor-tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="arrhythmia, diagnosis" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Curriculum mapping</Label>
            <Input data-testid="editor-curriculum" value={form.curriculum_mapping} onChange={(e) => set("curriculum_mapping", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>PARA content-map area</Label>
            <Input data-testid="editor-para" value={form.para_mapping} onChange={(e) => set("para_mapping", e.target.value)} />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Checkbox id="is_demo" data-testid="editor-is-demo" checked={form.is_demo} onCheckedChange={(v) => set("is_demo", !!v)} />
          <Label htmlFor="is_demo" className="font-normal text-sm">Show as a public demo question (only when published)</Label>
        </div>

        <div className="flex gap-3 pt-2">
          <Button data-testid="editor-save-btn" onClick={save} disabled={saving} className="px-8">
            {saving ? "Saving…" : isNew ? "Create as draft" : "Save changes"}
          </Button>
          <Button variant="outline" data-testid="editor-cancel-btn" onClick={() => navigate("/admin/questions")}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}
